<script lang="ts">
  /**
   * StrokesTab — every stroke, across your swims (Scott, 2026-09-10: "move all
   * the stroke info there"). A move out of the old Scores tab, not a new build:
   *
   *   1. Header (title + subtitle)
   *   2. YourStrokesSection — one card per stroke: typical / best / strokes
   *      per 25, and a trend (unchanged)
   *   3. StrokeTrendCard — the freestyle Glide trend (unchanged)
   *
   * The per-swim stroke charts (stroke mix, glide by stroke) stay with the swim
   * (Scott's Q1 ruling): this tab is history across swims, and a one-swim chart
   * here brought back the "26s vs 27s" scope mix-up. Both cards read caches, so
   * this view needs no derive engine for its own content.
   */
  import { onMount } from "svelte";
  import { workouts, loadWorkouts } from "../lib/workoutsStore";
  import StrokeTrendCard from "./StrokeTrendCard.svelte";
  import YourStrokesSection from "./YourStrokesSection.svelte";
  import { strokeInsightRows, swims25 } from "../lib/strokeInsights";
  import { medianDurationByStroke } from "../lib/recordEligibility";
  import { buildStrokeTrendViewModel } from "../lib/strokeTrend";
  import { getAllStrokeStats } from "../lib/strokeStats";
  import { getAllSwimFacts, strokesCoverage } from "../lib/allTimeAggregates";

  onMount(() => { void loadWorkouts(); });

  // The caches are localStorage reads, not stores: re-read when the workout
  // list lands (same accepted behavior the Scores tab had).
  let facts = $derived.by(() => {
    void $workouts;
    return getAllSwimFacts();
  });
  // The glide counts the Freestyle card's own swims: 25 m pool swims with a
  // Freestyle length (swims25). Its strokeStats cache has no pool filter, so it
  // also averaged 20 m swims (measured on the fixtures: 98 = the card's 92 + 6
  // swims in 20 m pools; VP panel round 2, 2026-09-11). Open-water swims stay
  // out of the denominator by construction; their count keeps the OW-only copy.
  let glideVm = $derived.by(() => {
    const free25 = new Set(swims25(facts, "Freestyle").map((e) => e.swimUuid));
    const openWaterCount = $workouts.filter((w) => w.swimmingLocationType === "openWater").length;
    return buildStrokeTrendViewModel(
      getAllStrokeStats().filter((s) => free25.has(s.swimUuid)),
      free25.size,
      openWaterCount,
    );
  });

  let medians = $derived(medianDurationByStroke(facts));
  let strokeRows = $derived(strokeInsightRows(facts, medians));
  let strokesCov = $derived(strokesCoverage(facts, $workouts.length));
</script>

<section class="strokes-tab" aria-label="Strokes">
  <!-- No subtitle: "Each stroke, across your swims." repeated the title and
       the section label (steering round 3, 2026-09-11). -->
  <div class="tab-head">
    <h1 class="tab-title">Strokes</h1>
  </div>

  <YourStrokesSection rows={strokeRows} coverage={strokesCov} />

  <StrokeTrendCard vm={glideVm} />
</section>

<style>
  .strokes-tab {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }

  /* Same header as the Sets tab (moved from the Scores tab unchanged). */
  .tab-head {
    padding: var(--space-2) 0 0;
  }
  .tab-title {
    font: var(--type-lede);
    letter-spacing: -0.01em;
    color: var(--text-primary);
    margin: 0;
  }
</style>
